import { Component, StrictMode, Suspense, lazy, useEffect, useState, type ComponentType, type ErrorInfo, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, HashRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { initializeAdminAuth, isAdminAuthenticated } from './lib/adminAuth';
import { hasSessionHint, initializeAuth, isAuthenticated } from './lib/auth';
import { buildHashRouteUrl, buildNormalizedHashUrl } from './lib/hashRoutes';
import './styles/global.css';
import './styles/workspace-ui.css';

const APP_RECOVERY_QUERY_KEY = '__dashboard_reload';

async function clearAppRecoveryState() {
  if (typeof window === 'undefined') return;

  try {
    const keys: string[] = [];
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index);
      if (key?.startsWith('lazy_reload_once:') || key?.startsWith('lazy_recover_once:')) {
        keys.push(key);
      }
    }
    keys.forEach((key) => window.sessionStorage.removeItem(key));
  } catch {
    // Best effort only.
  }

  try {
    if ('caches' in window) {
      const cacheKeys = await window.caches.keys();
      await Promise.all(cacheKeys.map((key) => window.caches.delete(key)));
    }
  } catch {
    // Some browsers or deployments may not expose Cache Storage.
  }
}

function buildRecoveryUrl(): string {
  const url = new URL(window.location.href);
  url.searchParams.set(APP_RECOVERY_QUERY_KEY, String(Date.now()));
  return url.toString();
}

async function recoverApplication() {
  if (typeof window === 'undefined') return;
  await clearAppRecoveryState();
  window.location.replace(buildRecoveryUrl());
}

function isChunkLoadError(error: unknown): boolean {
  const message = String((error as any)?.message || error || '').toLowerCase();
  return message.includes('failed to fetch dynamically imported module')
    || message.includes('importing a module script failed')
    || message.includes('chunkloaderror')
    || message.includes('loading chunk');
}

function lazyWithReloadOnChunkError<T extends ComponentType<any>>(
  loader: () => Promise<{ default: T }>,
  chunkKey: string
) {
  return lazy(async () => {
    try {
      const module = await loader();
      if (typeof window !== 'undefined') {
        sessionStorage.removeItem(`lazy_reload_once:${chunkKey}`);
        sessionStorage.removeItem(`lazy_recover_once:${chunkKey}`);
      }
      return module;
    } catch (error) {
      if (typeof window !== 'undefined' && isChunkLoadError(error)) {
        try {
          await new Promise((resolve) => window.setTimeout(resolve, 250));
          const retriedModule = await loader();
          sessionStorage.removeItem(`lazy_reload_once:${chunkKey}`);
          sessionStorage.removeItem(`lazy_recover_once:${chunkKey}`);
          return retriedModule;
        } catch (retryError) {
          if (!isChunkLoadError(retryError)) {
            throw retryError;
          }
        }
        const storageKey = `lazy_reload_once:${chunkKey}`;
        const alreadyReloaded = sessionStorage.getItem(storageKey) === '1';
        if (!alreadyReloaded) {
          sessionStorage.setItem(storageKey, '1');
          window.location.reload();
          return new Promise<never>(() => {});
        }
        const recoveryKey = `lazy_recover_once:${chunkKey}`;
        const alreadyRecovered = sessionStorage.getItem(recoveryKey) === '1';
        if (!alreadyRecovered) {
          sessionStorage.setItem(recoveryKey, '1');
          await recoverApplication();
          return new Promise<never>(() => {});
        }
      }
      throw error;
    }
  });
}

interface AppErrorBoundaryProps {
  hostType: ReturnType<typeof resolveHostType>;
  children: ReactNode;
}

interface AppErrorBoundaryState {
  error: Error | null;
}

class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = {
    error: null,
  };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Dashboard application render failed:', error, errorInfo);
  }

  render() {
    if (!this.state.error) {
      return this.props.children;
    }

    const isAdmin = this.props.hostType === 'admin';
    const title = isAdmin ? 'Admin failed to load' : 'Dashboard failed to load';
    const message = String(this.state.error?.message || 'Unexpected application error');

    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '1rem',
        minHeight: '100vh',
        padding: '1.5rem',
        background: isAdmin
          ? 'radial-gradient(circle at 80% -10%, rgba(30, 58, 138, 0.88) 0%, rgba(30, 58, 138, 0) 44%), linear-gradient(180deg, #05070d 0%, #04050a 100%)'
          : 'radial-gradient(circle at 18% -12%, rgba(96, 165, 250, 0.15) 0%, rgba(96, 165, 250, 0) 42%), radial-gradient(circle at 85% -28%, rgba(59, 130, 246, 0.14) 0%, rgba(59, 130, 246, 0) 48%), linear-gradient(180deg, #0b1f43 0%, #081733 44%, #06122a 74%, #040d20 100%)',
        color: '#dbeafe',
        textAlign: 'center',
      }}>
        <div style={{
          width: '100%',
          maxWidth: '32rem',
          display: 'grid',
          gap: '0.85rem',
          padding: '1.4rem',
          borderRadius: '1.25rem',
          border: '1px solid rgba(125, 194, 255, 0.22)',
          background: 'rgba(4, 12, 26, 0.82)',
          boxShadow: '0 20px 44px rgba(2, 8, 23, 0.34)',
        }}>
          <h1 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700 }}>{title}</h1>
          <p style={{ margin: 0, color: '#bfdbfe', lineHeight: 1.6 }}>
            The app hit a runtime error while loading this page.
          </p>
          <code style={{
            display: 'block',
            padding: '0.8rem 0.95rem',
            borderRadius: '0.9rem',
            background: 'rgba(2, 6, 23, 0.72)',
            color: '#e2e8f0',
            fontSize: '0.8rem',
            textAlign: 'left',
            wordBreak: 'break-word',
          }}>
            {message}
          </code>
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '0.7rem',
          }}>
            <button
              type="button"
              onClick={() => { void recoverApplication(); }}
              style={{
                minHeight: '2.8rem',
                borderRadius: '0.95rem',
                border: '1px solid transparent',
                background: 'linear-gradient(135deg, #5eb7ff 0%, #3779ff 100%)',
                color: '#f8fbff',
                fontSize: '0.95rem',
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Reload App
            </button>
            <button
              type="button"
              onClick={() => window.location.assign(isAdmin ? buildNormalizedHashUrl('/login') : buildNormalizedHashUrl('/login'))}
              style={{
                minHeight: '2.8rem',
                borderRadius: '0.95rem',
                border: '1px solid rgba(129, 170, 221, 0.28)',
                background: 'rgba(7, 18, 36, 0.56)',
                color: '#dbeafe',
                fontSize: '0.95rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Go To Login
            </button>
          </div>
        </div>
      </div>
    );
  }
}

const Landing = lazyWithReloadOnChunkError(() => import('./pages/Landing'), 'landing');
const HowItWorks = lazyWithReloadOnChunkError(() => import('./pages/HowItWorks'), 'how-it-works');
const PricingPage = lazyWithReloadOnChunkError(() => import('./pages/Pricing'), 'pricing');
const FaqPage = lazyWithReloadOnChunkError(() => import('./pages/Faq'), 'faq');
const About = lazyWithReloadOnChunkError(() => import('./pages/About'), 'about');
const Privacy = lazyWithReloadOnChunkError(() => import('./pages/Privacy'), 'privacy');
const Terms = lazyWithReloadOnChunkError(() => import('./pages/Terms'), 'terms');
const Contact = lazyWithReloadOnChunkError(() => import('./pages/Contact'), 'contact');
const Login = lazyWithReloadOnChunkError(() => import('./pages/Login'), 'login');
const Register = lazyWithReloadOnChunkError(() => import('./pages/Register'), 'register');
const ForgotPassword = lazyWithReloadOnChunkError(() => import('./pages/ForgotPassword'), 'forgot-password');
const Dashboard = lazyWithReloadOnChunkError(() => import('./pages/Dashboard'), 'dashboard');
const Settings = lazyWithReloadOnChunkError(() => import('./pages/Settings'), 'settings');
const Project = lazyWithReloadOnChunkError(() => import('./pages/Project'), 'project');
const Domains = lazyWithReloadOnChunkError(() => import('./pages/Domains'), 'domains');
const ApiAccess = lazyWithReloadOnChunkError(() => import('./pages/ApiAccess'), 'api-access');
const EndpointEditor = lazyWithReloadOnChunkError(() => import('./pages/EndpointEditor'), 'endpoint-editor');
const Analytics = lazyWithReloadOnChunkError(() => import('./pages/Analytics'), 'analytics');
const CronJobs = lazyWithReloadOnChunkError(() => import('./pages/CronJobs'), 'cron-jobs');
const Billing = lazyWithReloadOnChunkError(() => import('./pages/Billing'), 'billing');
const Support = lazyWithReloadOnChunkError(() => import('./pages/Support'), 'support');
const Docs = lazyWithReloadOnChunkError(() => import('./pages/Docs'), 'docs');
const AdminLogin = lazyWithReloadOnChunkError(() => import('./pages/AdminLogin'), 'admin-login');
const AdminPanel = lazyWithReloadOnChunkError(() => import('./pages/AdminPanel'), 'admin-panel');
const AdminSupport = lazyWithReloadOnChunkError(() => import('./pages/AdminSupport'), 'admin-support');

const BASE_DOMAIN = import.meta.env.VITE_BASE_DOMAIN || 'apiruntime.dev';

let dashboardAuthBootstrapped = false;
let dashboardAuthInitPromise: Promise<boolean | null> | null = null;
let adminAuthBootstrapped = false;
let adminAuthInitPromise: Promise<boolean> | null = null;

function resolveInitialRoutePath(hostType: ReturnType<typeof resolveHostType>): string {
  if (typeof window === 'undefined') return '/';
  if (hostType === 'admin' || hostType === 'dashboard') {
    const hash = String(window.location.hash || '');
    if (hash.startsWith('#/')) {
      const [hashPath] = hash.slice(1).split('?');
      return hashPath || '/';
    }
  }
  return window.location.pathname || '/';
}

function prefetchInitialRouteModule() {
  if (typeof window === 'undefined') return;
  const hostType = resolveHostType();
  const routePath = resolveInitialRoutePath(hostType).toLowerCase();

  if (hostType === 'admin') {
    if (routePath === '/support') {
      void import('./pages/AdminSupport');
      return;
    }
    if (routePath === '/login') {
      void import('./pages/AdminLogin');
      return;
    }
    void import('./pages/AdminPanel');
    return;
  }

  if (hostType === 'dashboard') {
    if (routePath === '/' || routePath === '/dashboard' || routePath === '/projects') {
      void import('./pages/Dashboard');
      return;
    }
    if (routePath.startsWith('/projects/')) {
      void import('./pages/Project');
      return;
    }
    if (routePath.startsWith('/edit-endpoint/') || routePath.startsWith('/create-endpoint/')) {
      void import('./pages/EndpointEditor');
      return;
    }
    if (routePath === '/login') {
      void import('./pages/Login');
      return;
    }
    if (routePath === '/support') {
      void import('./pages/Support');
      return;
    }
    if (routePath === '/register') {
      void import('./pages/Register');
      return;
    }
    if (routePath === '/forgot-password') {
      void import('./pages/ForgotPassword');
      return;
    }
  }

  if (routePath === '/' || routePath === '') {
    void import('./pages/Landing');
    return;
  }
  if (routePath.startsWith('/docs')) {
    void import('./pages/Docs');
    return;
  }
  if (routePath === '/pricing') {
    void import('./pages/Pricing');
    return;
  }
  if (routePath === '/faq' || routePath === '/pricing-faq') {
    void import('./pages/Faq');
    return;
  }
  if (routePath === '/how-it-works') {
    void import('./pages/HowItWorks');
    return;
  }
  if (routePath === '/about') {
    void import('./pages/About');
    return;
  }
  if (routePath === '/privacy') {
    void import('./pages/Privacy');
    return;
  }
  if (routePath === '/terms') {
    void import('./pages/Terms');
    return;
  }
  if (routePath === '/contact') {
    void import('./pages/Contact');
  }
}

prefetchInitialRouteModule();

async function ensureDashboardAuthReady(): Promise<boolean | null> {
  if (isAuthenticated()) {
    dashboardAuthBootstrapped = true;
    return true;
  }
  if (dashboardAuthBootstrapped) {
    return isAuthenticated();
  }
  if (!dashboardAuthInitPromise) {
    dashboardAuthInitPromise = (async () => {
      const result = await initializeAuth();
      dashboardAuthBootstrapped = result !== 'retryable';
      if (result === 'authenticated') return true;
      if (result === 'retryable') return null;
      return false;
    })().finally(() => {
      dashboardAuthInitPromise = null;
    });
  }
  return dashboardAuthInitPromise;
}

async function ensureAdminAuthReady(): Promise<boolean> {
  if (isAdminAuthenticated()) {
    adminAuthBootstrapped = true;
    return true;
  }
  if (adminAuthBootstrapped) {
    return isAdminAuthenticated();
  }
  if (!adminAuthInitPromise) {
    adminAuthInitPromise = (async () => {
      await initializeAdminAuth();
      adminAuthBootstrapped = true;
      return isAdminAuthenticated();
    })().finally(() => {
      adminAuthInitPromise = null;
    });
  }
  return adminAuthInitPromise;
}

function getDashboardUrl(path: string): string {
  const protocol = window.location.protocol;
  return buildHashRouteUrl(path, undefined, `${protocol}//dashboard.${BASE_DOMAIN}`);
}

function getMainSiteUrl(path: string): string {
  const protocol = window.location.protocol;
  return `${protocol}//${BASE_DOMAIN}${path}`;
}

function resolveHostType(): 'admin' | 'docs' | 'dashboard' | 'main' {
  if (typeof window === 'undefined') return 'main';
  const hostname = window.location.hostname.toLowerCase();
  if (hostname.startsWith('admin.')) return 'admin';
  if (hostname.startsWith('docs.')) return 'docs';
  if (hostname.startsWith('dashboard.') || hostname === 'localhost') return 'dashboard';
  return 'main';
}

function normalizeInternalHashUrl(hostType: 'admin' | 'docs' | 'dashboard' | 'main') {
  if (typeof window === 'undefined') return;
  if (hostType !== 'admin' && hostType !== 'dashboard') return;
  if (window.location.hash.startsWith('#/')) return;

  const path = window.location.pathname || '/';
  const search = window.location.search || '';
  if (path === '/' && !search) return;

  window.history.replaceState(null, '', buildNormalizedHashUrl(path, search));
}

/** Redirects to an external URL (used for cross-subdomain navigation). */
function ExternalRedirect({ url }: { url: string }) {
  useEffect(() => {
    window.location.replace(url);
  }, [url]);
  return null;
}

function resolveMemberTitle(pathname: string): string {
  const path = pathname.toLowerCase();
  return path.startsWith('/docs') ? 'ApiRunTime Docs' : 'ApiRunTime';
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function RouteTitleManager({ isAdminHost }: { isAdminHost: boolean }) {
  const location = useLocation();

  useEffect(() => {
    if (isAdminHost) {
      document.title = 'ApiRunTime';
      return;
    }

    document.title = resolveMemberTitle(location.pathname);
  }, [isAdminHost, location.pathname]);

  return null;
}

function LoadingScreen({ hostType }: { hostType?: ReturnType<typeof resolveHostType> }) {
  const resolvedHostType = hostType ?? resolveHostType();
  const isAdmin = resolvedHostType === 'admin';
  const loadingLabel = isAdmin
    ? 'Loading admin...'
    : (resolvedHostType === 'dashboard' ? 'Loading dashboard...' : 'Loading...');

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '12px',
      minHeight: '100vh',
      background: isAdmin
        ? 'radial-gradient(circle at 80% -10%, rgba(30, 58, 138, 0.88) 0%, rgba(30, 58, 138, 0) 44%), linear-gradient(180deg, #05070d 0%, #04050a 100%)'
        : 'radial-gradient(circle at 18% -12%, rgba(96, 165, 250, 0.15) 0%, rgba(96, 165, 250, 0) 42%), radial-gradient(circle at 85% -28%, rgba(59, 130, 246, 0.14) 0%, rgba(59, 130, 246, 0) 48%), linear-gradient(180deg, #0b1f43 0%, #081733 44%, #06122a 74%, #040d20 100%)',
      color: '#dbeafe',
    }}>
      <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="9" fill="none" stroke="rgba(147, 197, 253, 0.25)" strokeWidth="2" />
        <path d="M12 3a9 9 0 0 1 9 9" fill="none" stroke="#93c5fd" strokeWidth="2" strokeLinecap="round">
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 12 12"
            to="360 12 12"
            dur="0.75s"
            repeatCount="indefinite"
          />
        </path>
      </svg>
      <p style={{ margin: 0, fontSize: '13px', letterSpacing: '0.03em' }}>
        {loadingLabel}
      </p>
    </div>
  );
}

function DeferredLoadingScreen({
  hostType,
  delayMs = 320,
}: {
  hostType?: ReturnType<typeof resolveHostType>;
  delayMs?: number;
}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setVisible(true);
    }, delayMs);

    return () => {
      window.clearTimeout(timer);
    };
  }, [delayMs]);

  if (!visible) return null;
  return <LoadingScreen hostType={hostType} />;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(() => !dashboardAuthBootstrapped && !isAuthenticated());
  const [authenticated, setAuthenticated] = useState(() => isAuthenticated());

  useEffect(() => {
    let cancelled = false;
    let retryTimer: number | null = null;

    const run = async () => {
      const authResult = await ensureDashboardAuthReady();
      if (cancelled) return;

      if (authResult === null && hasSessionHint()) {
        setLoading(true);
        retryTimer = window.setTimeout(() => {
          void run();
        }, 1500);
        return;
      }

      setAuthenticated(Boolean(authResult));
      setLoading(false);
    };

    void run();

    return () => {
      cancelled = true;
      if (retryTimer !== null) {
        window.clearTimeout(retryTimer);
      }
    };
  }, []);

  if (loading) return <DeferredLoadingScreen hostType="dashboard" delayMs={360} />;

  return authenticated ? <>{children}</> : <Navigate to="/login" />;
}

function ProtectedAdminRoute({ children }: { children: React.ReactNode }) {
  const [loading, setLoading] = useState(() => !adminAuthBootstrapped && !isAdminAuthenticated());
  const [authenticated, setAuthenticated] = useState(() => isAdminAuthenticated());

  useEffect(() => {
    let cancelled = false;
    void ensureAdminAuthReady().then((isAuthed) => {
      if (cancelled) return;
      setAuthenticated(isAuthed);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) return <DeferredLoadingScreen hostType="admin" delayMs={360} />;

  return authenticated ? <>{children}</> : <Navigate to="/login" />;
}

function App() {
  const hostType = resolveHostType();
  normalizeInternalHashUrl(hostType);
  const isAdminHost = hostType === 'admin';
  const isDashboardHost = hostType === 'dashboard';
  const isDocsHost = hostType === 'docs';

  useEffect(() => {
    if (!isAdminHost && !isDashboardHost) return;
    const init = async () => {
      try {
        if (isAdminHost) {
          await ensureAdminAuthReady();
        } else {
          await ensureDashboardAuthReady();
        }
      } catch {
        // Protected routes handle auth state; failures here should not block rendering.
      }
    };
    void init();
  }, [isAdminHost, isDashboardHost]);

  useEffect(() => {
    document.body.classList.toggle('dashboard-blue-theme', !isAdminHost);
    return () => {
      document.body.classList.remove('dashboard-blue-theme');
    };
  }, [isAdminHost]);

  if (isAdminHost) {
    return (
      <HashRouter>
        <ScrollToTop />
        <RouteTitleManager isAdminHost />
        <AppErrorBoundary hostType="admin">
          <Suspense fallback={<DeferredLoadingScreen hostType="admin" delayMs={320} />}>
            <Routes>
              <Route path="/login" element={<AdminLogin />} />
              <Route
                path="/support"
                element={(
                  <ProtectedAdminRoute>
                    <AdminSupport />
                  </ProtectedAdminRoute>
                )}
              />
              <Route
                path="/*"
                element={(
                  <ProtectedAdminRoute>
                    <AdminPanel />
                  </ProtectedAdminRoute>
                )}
              />
            </Routes>
          </Suspense>
        </AppErrorBoundary>
      </HashRouter>
    );
  }

  if (isDashboardHost) {
    return (
      <HashRouter>
        <ScrollToTop />
        <RouteTitleManager isAdminHost={false} />
        <AppErrorBoundary hostType="dashboard">
          <Suspense fallback={<DeferredLoadingScreen hostType="dashboard" delayMs={320} />}>
            <Routes>
              <Route path="/docs/*" element={<Docs />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route
                path="/projects"
                element={
                  <ProtectedRoute>
                    <Dashboard />
                  </ProtectedRoute>
                }
              />
              <Route path="/dashboard" element={<Navigate to="/projects" replace />} />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <Settings />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/analytics"
                element={
                  <ProtectedRoute>
                    <Analytics />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/domains"
                element={
                  <ProtectedRoute>
                    <Domains />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/api"
                element={
                  <ProtectedRoute>
                    <ApiAccess />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/cron-jobs"
                element={
                  <ProtectedRoute>
                    <CronJobs />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/billing"
                element={
                  <ProtectedRoute>
                    <Billing />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/support"
                element={
                  <ProtectedRoute>
                    <Support />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/create-endpoint/:projectId"
                element={
                  <ProtectedRoute>
                    <EndpointEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/edit-endpoint/:endpointId"
                element={
                  <ProtectedRoute>
                    <EndpointEditor />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/projects/:projectId"
                element={
                  <ProtectedRoute>
                    <Project />
                  </ProtectedRoute>
                }
              />
              <Route path="/pricing" element={<ExternalRedirect url={getMainSiteUrl('/pricing')} />} />
              <Route path="/faq" element={<ExternalRedirect url={getMainSiteUrl('/faq')} />} />
              <Route path="/pricing-faq" element={<ExternalRedirect url={getMainSiteUrl('/faq')} />} />
              <Route path="/how-it-works" element={<ExternalRedirect url={getMainSiteUrl('/how-it-works')} />} />
              <Route path="/about" element={<ExternalRedirect url={getMainSiteUrl('/about')} />} />
              <Route path="/privacy" element={<ExternalRedirect url={getMainSiteUrl('/privacy')} />} />
              <Route path="/terms" element={<ExternalRedirect url={getMainSiteUrl('/terms')} />} />
              <Route path="/contact" element={<ExternalRedirect url={getMainSiteUrl('/contact')} />} />
              <Route path="/" element={<Navigate to="/projects" replace />} />
              <Route path="*" element={<Navigate to="/projects" replace />} />
            </Routes>
          </Suspense>
        </AppErrorBoundary>
      </HashRouter>
    );
  }

  // Main domain (apiruntime.dev) - public pages only
  return (
    <BrowserRouter>
      <ScrollToTop />
      <RouteTitleManager isAdminHost={false} />
      <AppErrorBoundary hostType="main">
        <Suspense fallback={<DeferredLoadingScreen hostType="main" delayMs={420} />}>
          <Routes>
            <Route path="/docs/*" element={<Docs />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/faq" element={<FaqPage />} />
            <Route path="/pricing-faq" element={<Navigate to="/faq" replace />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/about" element={<About />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/contact" element={<Contact />} />
            {/* Auth routes redirect to dashboard subdomain */}
            <Route path="/login" element={<ExternalRedirect url={getDashboardUrl('/login')} />} />
            <Route path="/register" element={<ExternalRedirect url={getDashboardUrl('/register')} />} />
            <Route path="/forgot-password" element={<ExternalRedirect url={getDashboardUrl('/forgot-password')} />} />
            <Route path="/projects" element={<ExternalRedirect url={getDashboardUrl('/projects')} />} />
            <Route path="/projects/:projectId" element={<ExternalRedirect url={getDashboardUrl(window.location.pathname)} />} />
            <Route path="/settings" element={<ExternalRedirect url={getDashboardUrl('/settings')} />} />
            <Route path="/billing" element={<ExternalRedirect url={getDashboardUrl('/billing')} />} />
            <Route path="/support" element={<ExternalRedirect url={getDashboardUrl('/support')} />} />
            <Route path="/" element={isDocsHost ? <Navigate to="/docs/getting-started" replace /> : <Landing />} />
            <Route path="*" element={<Navigate to={isDocsHost ? '/docs/getting-started' : '/'} replace />} />
          </Routes>
        </Suspense>
      </AppErrorBoundary>
    </BrowserRouter>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>
);
